/**
 * EnergyDilemmaModal.tsx — N-1 Energy Dilemma minigame (R16)
 *
 * Single-phase decision modal with 3 deterministic tradeoff options.
 * Mirrors ScoutModal single-phase + activeXxx local-latch pattern (R13 fix).
 *
 * Bug #5 fix: added intro pitch line + scenario-specific context so players
 * understand why the modal appeared.  After choosing, a 2-second result beat
 * shows the applied deltas before the modal closes.
 *
 * VISIBLE WHEN: pendingEnergyDilemma !== null AND boardAnimationActive === false
 * TEST PATH: era >= 2 → roll dice → land on terrain tile (Plains / Forest / Coast
 *            / River / Tundra / Mountain / Desert / Volcanic / Urban)
 *            → EnergyDilemmaModal renders after HexBoard walk animation completes.
 *            Three option buttons shown; player MUST pick one (no skip path).
 *            Choosing an option applies deltas to current seat and closes modal.
 *
 * Reduced-motion: no animations used (static decision modal — spec §0.1 rule 9).
 */

import type React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  ENERGY_DILEMMA_OPTIONS,
  type EnergyDilemmaOption,
  type EnergyDilemmaScenarioKey,
  _eraScale,
  useGameStore,
} from "../../store/game-store.js";
import styles from "./EnergyDilemmaModal.module.css";

// ---------------------------------------------------------------------------
// Scenario display metadata keyed by optionsKey (R21-GF-7: all 9 scenarios)
// ---------------------------------------------------------------------------

const SCENARIO_META: Record<
  EnergyDilemmaScenarioKey,
  { icon: string; pitch: string; scenarioText: string }
> = {
  scarcity: {
    icon: "⚡",
    pitch: "Energy crisis! Your civilization must decide how to respond to a shortage.",
    scenarioText:
      "Energy reserves are running low. The grid is under stress — choose how to respond.",
  },
  abundance: {
    icon: "🌞",
    pitch: "Energy windfall! Renewables are generating more than the grid can use right now.",
    scenarioText:
      "Renewable output is higher than demand today. Decide how best to use the surplus.",
  },
  grid_strain: {
    icon: "🔌",
    pitch: "Grid emergency! Peak demand is overwhelming your infrastructure — act now.",
    scenarioText:
      "Peak demand is straining the grid. Every decision now has trade-offs for the network.",
  },
  subsidy_debate: {
    icon: "🏛️",
    pitch: "Budget decision! Policymakers are debating where to direct energy subsidies.",
    scenarioText:
      "The government has a limited subsidy budget. Where you direct it shapes the energy future.",
  },
  carbon_pricing: {
    icon: "💨",
    pitch: "Carbon policy vote! Setting the right price on carbon drives investment and emissions.",
    scenarioText:
      "Carbon pricing policy is up for review. The level you set affects both the treasury and the atmosphere.",
  },
  grid_modernization: {
    icon: "🔧",
    pitch: "Infrastructure moment! Ageing grid assets need attention — invest now or defer.",
    scenarioText: "The electricity grid is showing its age. Choose how aggressively to modernize.",
  },
  coal_jobs_vs_solar_jobs: {
    icon: "👷",
    pitch:
      "Workforce crossroads! Coal communities and solar growth are pulling in opposite directions.",
    scenarioText:
      "The energy transition is disrupting jobs. How will your region handle the workforce shift?",
  },
  trade_tariff: {
    icon: "📦",
    pitch:
      "Trade policy! Import tariffs on energy equipment can protect domestic industry — or slow clean tech.",
    scenarioText:
      "Renewable equipment tariff levels are being set. Your choice shapes both the economy and emissions.",
  },
  coastal_vs_inland_transmission: {
    icon: "🗺️",
    pitch:
      "Grid expansion! Where you build the next transmission corridor determines future energy mix.",
    scenarioText:
      "A major new transmission investment is needed. Coastal, inland, or decentralized — each path has trade-offs.",
  },
};

function formatDelta(value: number, positive: string): string {
  if (value === 0) return "–";
  return `${value > 0 ? "+" : ""}${value}${positive}`;
}

// ---------------------------------------------------------------------------
// Main EnergyDilemmaModal — latch + render gate
// ---------------------------------------------------------------------------

export function EnergyDilemmaModal(): React.ReactElement | null {
  const boardAnimationActive = useGameStore((s) => s.boardAnimationActive);
  const pendingEnergyDilemma = useGameStore((s) => s.pendingEnergyDilemma);
  const submitEnergyDilemma = useGameStore((s) => s.submitEnergyDilemma);
  const era = useGameStore((s) => s.era);

  // R13 Codex-B HIGH 3: latch locally so the modal survives pendingEnergyDilemma
  // being cleared by submit before the user sees a flash.
  const [activeDilemma, setActiveDilemma] = useState<{
    tileIdx: number;
    terrain: string;
    optionsKey: string;
  } | null>(null);

  useEffect(() => {
    if (pendingEnergyDilemma !== null && activeDilemma === null) {
      setActiveDilemma({
        tileIdx: pendingEnergyDilemma.tileIdx,
        terrain: pendingEnergyDilemma.terrain,
        optionsKey: pendingEnergyDilemma.optionsKey,
      });
    }
  }, [pendingEnergyDilemma, activeDilemma]);

  if (boardAnimationActive) return null;
  if (activeDilemma === null) return null;

  return (
    <EnergyDilemmaModalInner
      key={`energy-dilemma-${activeDilemma.tileIdx}-${activeDilemma.optionsKey}`}
      terrain={activeDilemma.terrain}
      optionsKey={activeDilemma.optionsKey}
      era={era}
      submitEnergyDilemma={submitEnergyDilemma}
      onComplete={() => setActiveDilemma(null)}
    />
  );
}

// ---------------------------------------------------------------------------
// Inner (keyed to reset state on each new offer)
// ---------------------------------------------------------------------------

interface InnerProps {
  terrain: string;
  optionsKey: string;
  era: 1 | 2 | 3 | 4;
  submitEnergyDilemma: (choiceIdx: 0 | 1 | 2) => void;
  onComplete: () => void;
}

/** Bug #5: consequence narrative — 1-line plain-English outcome of the chosen
 *  option, shown in the RESULT BEAT *after* the player picks (NOT as a per-option
 *  preview before the choice; see user feedback 2026-06-06). Takes the already
 *  era-scaled tokens/pollution + raw energy so the wording can never contradict
 *  the numeric deltas rendered alongside it. */
function consequenceNarrative(tokens: number, pollution: number, energy: number): string {
  const parts: string[] = [];
  if (tokens !== 0)
    parts.push(
      tokens > 0
        ? `Gain ${tokens}T from efficiency`
        : `Spend ${Math.abs(tokens)}T on infrastructure`,
    );
  if (pollution !== 0)
    parts.push(pollution > 0 ? `+${pollution} pollution` : `${pollution} pollution (cleaner)`);
  if (energy !== 0)
    parts.push(energy > 0 ? `+${energy} energy stored` : `${Math.abs(energy)} energy consumed`);
  return parts.length > 0 ? parts.join(" · ") : "No immediate resource change.";
}

function EnergyDilemmaModalInner({
  terrain,
  optionsKey,
  era,
  submitEnergyDilemma,
  onComplete,
}: InnerProps): React.ReactElement {
  const key = optionsKey as EnergyDilemmaScenarioKey;
  const options: [EnergyDilemmaOption, EnergyDilemmaOption, EnergyDilemmaOption] =
    ENERGY_DILEMMA_OPTIONS[key] ?? ENERGY_DILEMMA_OPTIONS.scarcity;
  // R21-GF-7: fallback to scarcity if key is somehow unrecognized.
  const meta = SCENARIO_META[key] ?? SCENARIO_META.scarcity;
  // R21-GF-6: era scaling factor for result-beat display.
  const eraScaleFactor = _eraScale(Math.max(1, Math.min(4, era)) as 1 | 2 | 3 | 4);

  // Bug #5: result beat — show chosen option deltas for 2 s before closing
  const [resultBeat, setResultBeat] = useState<{
    label: string;
    tokens: number;
    pollution: number;
    energy: number;
    consequence: string;
  } | null>(null);
  const resultTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up timer on unmount
  useEffect(() => {
    return () => {
      if (resultTimerRef.current !== null) clearTimeout(resultTimerRef.current);
    };
  }, []);

  const handleChoose = useCallback(
    (idx: 0 | 1 | 2) => {
      const chosen = options[idx];
      submitEnergyDilemma(idx);
      // R21-GF-6: result beat shows SCALED deltas (matches what the store applied).
      const scaledTokens = Math.round(chosen.tokens * eraScaleFactor);
      const scaledPollution = Math.round(chosen.pollution * eraScaleFactor);
      setResultBeat({
        label: chosen.label,
        tokens: scaledTokens,
        pollution: scaledPollution,
        energy: chosen.energy, // energy does not scale
        // Consequence line built from the SAME scaled numbers shown below, so the
        // plain-English outcome and the numeric chips never disagree.
        consequence: consequenceNarrative(scaledTokens, scaledPollution, chosen.energy),
      });
      resultTimerRef.current = setTimeout(() => {
        setResultBeat(null);
        onComplete();
      }, 2000);
    },
    [submitEnergyDilemma, onComplete, options, eraScaleFactor],
  );

  // ── Result beat ──
  if (resultBeat !== null) {
    return (
      <div
        className={styles.overlay}
        // biome-ignore lint/a11y/useSemanticElements: overlay needs z-index stacking context
        role="dialog"
        aria-modal="true"
        aria-label="Energy Dilemma Result"
        data-testid="energy-dilemma-result"
      >
        <div className={styles.panel}>
          <div className={styles.header}>
            <span className={styles.icon} aria-hidden="true">
              {meta.icon}
            </span>
            <div className={styles.titleBlock}>
              <div className={styles.title}>DECISION MADE</div>
              <span className={styles.terrainBadge}>{terrain}</span>
            </div>
          </div>
          <p className={styles.resultChosen} data-testid="energy-dilemma-result-label">
            You chose: <strong>{resultBeat.label}</strong>
          </p>
          {/* Consequence (effect of the choice) revealed only AFTER the pick —
              never previewed as part of the option. */}
          <p className={styles.resultConsequence} data-testid="energy-dilemma-consequence">
            {resultBeat.consequence}
          </p>
          <div className={styles.resultDeltas} data-testid="energy-dilemma-result-deltas">
            <span className={styles.deltaTokens}>
              Tokens: {formatDelta(resultBeat.tokens, "T")}
            </span>
            <span className={styles.deltaPollution}>
              Pollution: {formatDelta(resultBeat.pollution, "")}
            </span>
            <span className={styles.deltaEnergy}>Energy: {formatDelta(resultBeat.energy, "")}</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={styles.overlay}
      // biome-ignore lint/a11y/useSemanticElements: overlay needs z-index stacking context
      role="dialog"
      aria-modal="true"
      aria-label="Energy Dilemma"
      data-testid="energy-dilemma-modal"
    >
      <div className={styles.panel}>
        {/* Header */}
        <div className={styles.header}>
          <span className={styles.icon} aria-hidden="true">
            {meta.icon}
          </span>
          <div className={styles.titleBlock}>
            <div className={styles.title}>ENERGY DILEMMA</div>
            <span className={styles.terrainBadge} data-testid="energy-dilemma-terrain">
              {terrain}
            </span>
          </div>
        </div>

        {/* Bug #5: intro pitch — explains why modal appeared */}
        <p className={styles.pitch} data-testid="energy-dilemma-pitch">
          {meta.pitch}
        </p>

        {/* Scenario description */}
        <p className={styles.scenario} data-testid="energy-dilemma-scenario">
          {meta.scenarioText}
        </p>

        {/* Option buttons — R20-GF-11: deltas HIDDEN during pick; only label +
            narrative shown. Deltas appear in the result beat after choice. */}
        <div
          className={styles.optionsList}
          data-testid="energy-dilemma-options"
          data-testid-extra="energy-dilemma-deltas-hidden"
        >
          {options.map((opt, idx) => (
            <button
              // biome-ignore lint/suspicious/noArrayIndexKey: stable 3-element constant array
              key={idx}
              type="button"
              className={styles.optionBtn}
              onClick={() => handleChoose(idx as 0 | 1 | 2)}
              data-testid={`energy-dilemma-option-${idx}`}
            >
              <span className={styles.optionLabel}>{opt.label}</span>
              {/* User feedback 2026-06-06: numeric deltas + qualitative narrative both hidden
                  before selection — outcome revealed only in result beat after pick.
                  Only the SDG education fact stays visible to inform the choice. */}
              <span className={styles.optionFact}>{opt.sdgFact}</span>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}
